import { computed, inject, ref, type App, type Ref } from 'vue'

import { messages, type Locale, type Messages } from './messages'

export type I18nApi = {
  locale: Ref<Locale>
  supportedLocales: readonly Locale[]
  setLocale: (next: Locale) => void
  t: (key: string, params?: Record<string, string | number>) => string
}

const STORAGE_KEY = 'perlingo.locale'

const getByPath = (obj: unknown, path: string): unknown => {
  if (!obj || typeof obj !== 'object') return undefined
  const parts = path.split('.').filter(Boolean)
  let cur: any = obj
  for (const part of parts) {
    if (!cur || typeof cur !== 'object') return undefined
    cur = cur[part]
  }
  return cur
}

const interpolate = (template: string, params?: Record<string, string | number>) => {
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (_m, name: string) => {
    const v = params[name]
    return v === undefined || v === null ? '' : String(v)
  })
}

export const detectInitialLocale = (supported: readonly Locale[]): Locale => {
  const normalize = (raw: string): Locale | null => {
    const base = raw.trim().toLowerCase().replace('_', '-').split('-')[0]
    if (!base) return null
    if (supported.includes(base as Locale)) return base as Locale
    return null
  }

  try {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null
    if (stored) {
      const hit = normalize(stored)
      if (hit) return hit
    }
  } catch {
    // ignore
  }

  // WordPress sets <html lang="…"> to the current site/admin language.
  try {
    const htmlLang = typeof document !== 'undefined' ? document.documentElement?.lang : ''
    if (htmlLang) {
      const hit = normalize(htmlLang)
      if (hit) return hit
    }
  } catch {
    // ignore
  }

  try {
    const nav = typeof navigator !== 'undefined' ? navigator.language : ''
    if (nav) {
      const hit = normalize(nav)
      if (hit) return hit
    }
  } catch {
    // ignore
  }

  return supported[0] || 'en'
}

export const createI18n = (opts?: {
  initialLocale?: Locale
  customMessages?: Record<Locale, Messages>
}) => {
  const supportedLocales = Object.keys(messages) as Locale[]
  const resolvedMessages = (
    opts?.customMessages ? { ...messages, ...opts.customMessages } : messages
  ) as Record<Locale, Messages>

  const initial = opts?.initialLocale || detectInitialLocale(supportedLocales)
  const locale = ref<Locale>(initial)

  const t = (key: string, params?: Record<string, string | number>) => {
    const l = locale.value
    const primary = getByPath(resolvedMessages[l], key)
    const fallback = getByPath(resolvedMessages.en, key)
    const raw =
      typeof primary === 'string' ? primary : typeof fallback === 'string' ? fallback : key
    return interpolate(raw, params)
  }

  const setLocale = (next: Locale) => {
    if (!supportedLocales.includes(next)) return
    locale.value = next
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // ignore
    }
  }

  const api: I18nApi = {
    locale,
    supportedLocales,
    setLocale,
    t,
  }

  const install = (app: App) => {
    app.provide('perlingo-i18n', api)
    // Templates can call $t('key') without importing anything.
    ;(app.config.globalProperties as any).$t = t
    ;(app.config.globalProperties as any).$setLocale = setLocale
    ;(app.config.globalProperties as any).$locale = computed(() => locale.value)
  }

  return { ...api, install }
}

export const perlingoI18n = createI18n()

export const useI18n = (): I18nApi => {
  const api = inject('perlingo-i18n', null) as I18nApi | null
  if (api) return api
  return perlingoI18n
}
